import { default as moment } from "moment";

import { ButtonVariant, ButtonView } from "common/views/button/Button.view";
import MutationButton from "common/views/button/MutationButton";
import IconView from "common/views/icon/Icon.view";
import { ImageVariant, ImageView } from "common/views/image/Image.view";
import PageLinkView from "common/views/pageLink/PageLink.view";
import { IDataFragment } from "core/components/FragmentProvider";
import UrlUtil from "core/util/UrlUtil";
import CatalogLabels from "panels/catalog/Catalog.labels";
import Course, { Variant } from "panels/catalog/entities/Course";
import ICourseFragmentProps from "./ICourseFragmentProps";
import { StyledNewBanner, StyledThumbnailOverlay } from "./styled";
import TokenSession from "core/util/session/TokenSession";
import SubscriptionExtend from "panels/catalog/entities/SubscriptionExtend";
import React, { useEffect, useState } from "react";
import Label from "core/components/label/Label";
import { IBreadcrumb } from "core/util/IBreadcrumb";
import { lastItem } from "core/util/ArrayUtil";
import { DialogView } from "common/views/dialog/Dialog.view";
import RenderVariants from "../renderVariants";
import ExtendSubscription from "./extendSubscription";
import { BlockView, BlockVariant } from "common/views/block/BlockView.view";
import { RateItem } from "panels/catalog/entities/Rating";
import events from "common/events";
import Rating from "react-rating";
import { MutationType } from "typegraph";
import TypographyView from "common/views/typographyView/Typography.view";
import { update } from "lodash";
import { Intent } from "@blueprintjs/core";
import { CatalogToaster } from "panels/catalog/catalogActions";
import { WaitingList } from "panels/catalog/entities/WaitingList";
import CourseBookmark from "panels/catalog/entities/CourseBookmark";

export const previewCourse: IDataFragment<ICourseFragmentProps> = {
	name: "Preview Course",
	label: { k: "course.preview", v: "Preview Course" },
	component: ({ course}) => {
		if (!course) {
			return null;
		}

		const handlePreview = (course) => {
			const width = 800; // Change as needed
            const height = 600; // Change as needed
            const left = (window.screen.width - width) / 2;
            const top = (window.screen.height - height) / 2;

            window.open(
                course.previewUrl,
                '_blank',
                `width=${width},height=${height},left=${left},top=${top},resizable,scrollbars`
            );
		}

		if (course && course.previewUrl) {
			return (
			<ButtonView
					variant={ButtonVariant.PRIMARY}
					viewKey="catalog.preview"
					onClick={() => handlePreview(course)}
				>
					<CatalogLabels.Labels.PreviewCourse />
				</ButtonView>
			);
		}
		return null;
	}
};


export const subsciptionExpired: IDataFragment<ICourseFragmentProps> = {
	name: "Subscription Expired",
	label: { k: "course.subscription", v: "Subscription Expired" },
	component: ({ course, detailsLinkVisible }) => {
		if (!course) {
			return null;
		}
		if (course && course.subscriptionEndDate && !course.canExtend && !course.activeSubscriptionExists) {
			return (<TypographyView viewKey="subscriptionexpire.title">
					 <CatalogLabels.Labels.subscriptionExpired />
				</TypographyView>)
		}
		return null;
	}
};

// launch course / primary action
export const launchCourse: IDataFragment<ICourseFragmentProps> = {
	name: "Launch Course",
	label: { k: "course.launchCourse", v: "Launch" },
	enabled: ({ course, detailsLinkVisible }) =>
		!(course && course.baseType === "base:program" && detailsLinkVisible),
	component: ({course, actions, launchable}) => {
		const [showRes,setRes]=useState("")		
		if (!course) {
			return <ButtonView variant={ButtonVariant.PRIMARY} viewKey="catalog.launchCourse">{'...'}</ButtonView>;
		}

		if (launchable === false) {
			return null;
		}

		if (course && course.subscriptionEndDate && !course.canExtend && !course.activeSubscriptionExists) {
			return null;
		}

		if(!course.approveWaiting && course.waitingOptionForCourse){
			return (
			<React.Fragment>
				
			<MutationButton
			variant={ButtonVariant.PRIMARY}
			  viewKey="course.waitingList"
			clazz={WaitingList}
			params={{ id: Number(course.metaId),userId:Number(TokenSession.getUserId()) }}
			onSuccess={(result)=>{
				setRes(result.entity.message);
			}}
			clearCacheOnSuccess={true}>
			 <CatalogLabels.Labels.WaitingList/>
			</MutationButton>

			{ showRes?(
			   CatalogToaster.show({
			   intent: Intent.PRIMARY,
			   message: showRes
		    })):null}
			</React.Fragment>
			);		
		}
		

		// launchable
		if (course.canLaunch) {
			return (
				<ButtonView
					variant={ButtonVariant.PRIMARY}
					viewKey="catalog.launchCourse"
					onClick={() => actions.launchCourse(course)}
					attrs={course && {
						// TODO move this
						'data-wap_type': 'pt-course-launch',
						'data-wap_ref': `${course.itemExternalId}:${course.title}`
					}}
					isFocusable
					onKeyDown={(e) => {
						if (e.key === "Enter") {
							actions.launchCourse(course);
						}
					}
					}

				>
					<CatalogLabels.Labels.LaunchCourse />
				</ButtonView>
			);
		}

		// can't launch, but still free to enroll
		if (!course.completion && course.variants && (!course.canBuy && course.variants.find((v) => v.price === 0 && !v.displayOnly))) {
			if(TokenSession.isAuthenticated()) {
			return (
				<MutationButton
					variant={ButtonVariant.PRIMARY}
					viewKey="catalog.enroll"
					clazz={Course}
					params={{id: course.metaId}}
					clearCacheOnSuccess={true}
				>
					<CatalogLabels.Labels.EnrollCourse />
				</MutationButton>
			);
		}
		return (
			<ButtonView
					variant={ButtonVariant.PRIMARY}
					viewKey="catalog.enroll"
					onClick={() => actions.enrollInCourse(course)}
				>
					<CatalogLabels.Labels.EnrollCourse />
				</ButtonView>
			);
		}

		return null;
	}
};

export const detailsButton: IDataFragment<ICourseFragmentProps> = {
	name: "Course Details Button",
	label: { k: "course.detailsButton", v: "Details" },
	component: ({ course, detailsLinkVisible }) => {
		if (detailsLinkVisible === false) {
			return false;
		}
		/**Finding  if variants  price = 0 then show only view details  button*/
		let variantList = [];
		if (course && course.variants && course.variants.length > 0) {
			variantList = course.variants.filter(variant => variant.price > 0);
		}
		if (course && course.baseType === "base:program" && !course.canBuy) {
			return (
				<PageLinkView
					attrs={
						course && {
							"data-wap_ref": `${course.itemExternalId}:${
								course.title
							}`
						}
					}
					params={{
						entryId: course && course.id,
						subscribed:
							course &&
							(course.activeSubscriptionExists ||
								course.canExtend),
						itemId:course && course.itemId
					}}
					viewKey="catalog.detailsLink.link"
					breadcrumbs={(target, breadcrumbs) => {
						let nextCrumbs: IBreadcrumb[] = [];
						const courseKey = "course:" + course.id;
						// push current page's crumb
						const lastCrumb = lastItem(breadcrumbs);

						const currentPath = UrlUtil.getCurrentPath();
						// if last crumb was for the current page and has a target, use that one
						if (
							lastCrumb &&
							lastCrumb.target.pathname === currentPath
						) {
							nextCrumbs = [
								...breadcrumbs.slice(0, breadcrumbs.length - 1),
								{
									...lastCrumb,
									target: {
										pathname: currentPath,
										search: UrlUtil.getSearch()
									}
								}
							];
						} else if (
							// if last crumb was for the current page and has no target path associated
							lastCrumb &&
							lastCrumb.target.pathname === ""
						) {
							nextCrumbs = [
								...breadcrumbs.slice(0, breadcrumbs.length - 1),
								{
									...lastCrumb,
									target: {
										pathname: currentPath,
										search: UrlUtil.getSearch()
									}
								}
							];
						} else {
							// else create a new one for the current page
							nextCrumbs = [
								...breadcrumbs,
								{
									key: "course-detail",
									label: {
										k: courseKey,
										v:
											window.parent.location.pathname.slice(
												1
											) || "Home"
									},
									target: {
										pathname: currentPath,
										search: UrlUtil.getSearch()
									}
								}
							];
						}

						// push article crumb
						nextCrumbs.push({
							key: courseKey,
							label: {
								k: courseKey,
								v: course.title
							},
							//target
							target: {
								pathname: "",
								search: ""
							}
						});

						return nextCrumbs;
					}}
				>
					<ButtonView
						variant={ButtonVariant.PRIMARY}
						viewKey="catalog.detailsButton.button"
					>
						<CatalogLabels.Labels.ViewDetails />
					</ButtonView>
				</PageLinkView>
			);
		}
		return null;
	}
};

// view details
export const detailsLink: IDataFragment<ICourseFragmentProps> = {
	name: "Course Details Link",
	label: { k: "course.detailsLink", v: "Details" },
	enabled: ({ course, detailsLinkVisible }) =>
		!(course && course.baseType === "base:program" && detailsLinkVisible && !course.canBuy),
	component: ({course,detailsLinkVisible}) => {
		if(detailsLinkVisible===false){
			return false;
		}
		return (
			<PageLinkView
				attrs={course && {
					'data-wap_ref': `${course.itemExternalId}:${course.title}`
				}}
				params={{entryId: course && course.id, subscribed : course && (course.activeSubscriptionExists || course.canExtend), itemId:course && course.itemId}}
				viewKey="catalog.detailsLink.link"
				breadcrumbs={(target, breadcrumbs) => {
					let nextCrumbs: IBreadcrumb[] = [];
					const courseKey =  "course:" + course.id;
					// push current page's crumb
					const lastCrumb = lastItem(breadcrumbs);

					const currentPath = UrlUtil.getCurrentPath();
					// if last crumb was for the current page and has a target, use that one
					if (
						lastCrumb &&
						lastCrumb.target.pathname === currentPath
					) {
						nextCrumbs = [
							...breadcrumbs.slice(0, breadcrumbs.length - 1),
							{
								...lastCrumb,
								target: {
									pathname: currentPath,
									search: UrlUtil.getSearch()
								}
							}
						];
					} else if ( // if last crumb was for the current page and has no target path associated
						lastCrumb &&
						lastCrumb.target.pathname === ''
					) {
						nextCrumbs = [
							...breadcrumbs.slice(0, breadcrumbs.length - 1),
							{
								...lastCrumb,
								target: {
									pathname: currentPath,
									search: UrlUtil.getSearch()
								}
							}
						];
					} else {
						// else create a new one for the current page
						nextCrumbs = [
							...breadcrumbs,
							{
								key: "course-detail",
								label: {
									k: courseKey,
									v: window.parent.location.pathname.slice(1) || "Home"
								},
								target: {
									pathname: currentPath,
									search: UrlUtil.getSearch()
								}
							},
						];
					}

					// push article crumb
					nextCrumbs.push({
						key: courseKey,
						label: {
							k: courseKey,
							v: course.title,
						},
						//target
						target: {
							pathname: '',
							search: ''
						}
					});

					return nextCrumbs;
				}}
			>
				<ButtonView variant={ButtonVariant.SECONDARY} viewKey="catalog.detailsLink.button">
					<CatalogLabels.Labels.ViewDetails />
				</ButtonView>
			</PageLinkView>
		);
	}
};

// clickable image
export const actionImage: IDataFragment<ICourseFragmentProps> = {
	name: "Action Image",
	label: {k: "course.actionImage", v: "Image"},
	component: ({course, actions, showNew, launchable}) => {
		return (
			<div style={{position: "relative"}}>
				<ImageView
					src={course && (course.iconUrl || course.smallIconUrl)}
					overlay={course && course.productType && {
						src: course.productType.iconUrl,
						position: "top left"
					}}
					variant={ImageVariant.THUMBNAIL}
					viewKey="course.image.actions"
				/>
				{
					(showNew && course && course.startDate && moment(new Date()).diff(course.startDate, 'days') < 30) && (
						<StyledNewBanner>
							<CatalogLabels.Labels.New />
						</StyledNewBanner>
					)
				}
				{course && course.launchURL && launchable && (
					<StyledThumbnailOverlay onClick={() => actions.launchCourse(course)}
					attrs={course && {
						// TODO move this
						'data-wap_type': 'pt-course-launch',
						'data-wap_ref': `${course.itemExternalId}:${course.title}`
					}}>
						<IconView icon="play" viewKey="course-play-icon" />
					</StyledThumbnailOverlay>
				)}
			</div>
		);
	}
};


// vote up

// favorite

// add to cart
export const buy: IDataFragment<ICourseFragmentProps> = {
	name: "Buy",
	label: {k: "course.buy", v: "Purchase"},
	enabled: ({course}) => !course || (course && course.canBuy), // skeleton when loading, hide if loaded and not buyable
	component: ({ course, actions, disableDirectBuy, buySSORedirectPage, loginBeforeBuy }) => {
		const [isOpen, toggleOpen] = useState(false);
		const [variants, setVariants] = useState([]);

		const _onclickHandler = () => {

			if (buySSORedirectPage && (!TokenSession.getUser() || !TokenSession.getUser().getId())) {
				window.location.replace(UrlUtil.getGatewayRedirectLink(
					buySSORedirectPage,
					{ entryId: course.id }
				));

				return;
			}

			if (variants.length === 1)
				actions.addToCart(course, variants[0], disableDirectBuy, loginBeforeBuy);
			else
				toggleOpen(true);
		}

		useEffect(() => {
			//here filtering variants
			if (course && course.variants && course.variants.length > 0) {
				const variantList = course.variants.filter(variant => !variant.repurchaseVariant && (course.canBuy || variant.price > 0) && !variant.displayOnly)
				setVariants(variantList);
			}

		}, [course])

		return (
			course && variants && variants.length > 0 &&
			<React.Fragment>
				<DialogView isOpen={isOpen} viewKey="dialog" onClosed={() => toggleOpen(!isOpen)}>
					<RenderVariants variants={variants}
						addTocart={(v: Variant) => actions.addToCart(course, v, disableDirectBuy, loginBeforeBuy)}
						toggleOpen={() => toggleOpen(!isOpen)}
						isRepurchase={false} />
				</DialogView>
				<ButtonView
					variant={ButtonVariant.PRIMARY}
					viewKey="catalog.buyCourse"
					onClick={() => _onclickHandler()}
				>
					{variants.length == 1 ?
						(<CatalogLabels.Labels.BuyCourse args={{ price: variants[0].price, title: variants[0].title }} />)
						: <p>Add to Cart</p>
					}
				</ButtonView>
			</React.Fragment>
		)


	}
};

// vote up

// favorite

// add to buy Course
export const buyCourse: IDataFragment<ICourseFragmentProps> = {
	name: "Course Buy",
	label: { k: "buy.course", v: "Course buy" },
	enabled: ({ course }) => !course || (course && course.canBuy), // skeleton when loading, hide if loaded and not buyable
	component: ({ course, actions, disableDirectBuy, loginBeforeBuy, pageActions, cartItems, quantityInCart,buySSORedirectPage }) => {
		const [isOpen, toggleOpen] = useState(false);
		const [variants, setVariants]= useState([]);
		const [courseAdded, setCourseAdded] = useState(false);

		const _onclickHandler=()=>{

			if(buySSORedirectPage && (!TokenSession.getUser() || !TokenSession.getUser().getId())){
				window.location.replace(UrlUtil.getGatewayRedirectLink(
					buySSORedirectPage,
					{entryId:course.id}
				));

				return;
			}

			
			if (variants.length === 1) {
				actions.addToBuy(course, variants[0], disableDirectBuy, loginBeforeBuy, pageActions, quantityInCart);
				setCourseAdded(true);
			}
			else 
				toggleOpen(true);
		}


		useEffect(() => {
			//here filtering variants
			if(course && course.variants && course.variants.length >0){
				const variantList= course.variants.filter(variant => !variant.repurchaseVariant && (course.canBuy || variant.price > 0) && !variant.displayOnly)
				setVariants(variantList);
			}
			if (cartItems && cartItems.length) {
				cartItems.map((item) => {
					if (item['id'] === course.id) {
						setCourseAdded(true);
					}
				})
			}

		}, [course, courseAdded, cartItems])

		const handleAddCourseVariant = (variant) => { // this is to add correct variant and quantity of each variant in cart
			const filterVariants = course.variants.filter(item => (item.id === variant.id));
			const updatedCourse = { ...course };
			updatedCourse.variants = filterVariants;
			pageActions.addItemsToCart([updatedCourse]);
			pageActions.setQuantityInCart([...quantityInCart, { courseId: course.id, courseQuantity: 1 }]);
			setTimeout(function () {
				if ([course].length) {
					window.dispatchEvent(events.CartUpdated);
					CatalogToaster.show({
						intent: Intent.SUCCESS,
						message: "Added to cart"
					})
				}
			}, 1000);
		}

		return (
			course && variants && variants.length>0 &&
			<React.Fragment>
				<DialogView isOpen={isOpen} viewKey="dialog" onClosed={() => toggleOpen(!isOpen)}>
						<RenderVariants variants={variants} 
							addTocart={(v: Variant) => handleAddCourseVariant(v)}
								toggleOpen={() => toggleOpen(!isOpen)}
								isRepurchase={false} />
				</DialogView>
					{!courseAdded ? (
				<ButtonView
					variant={ButtonVariant.PRIMARY}
					viewKey="catalog.buyCourse"
					onClick={() => _onclickHandler()}
				>
				{variants.length ==1 ?
								(<CatalogLabels.Labels.CourseBuy args={{ price: variants[0].price, title: variants[0].title }} />)
								: <Label k={"user.buy.course"} v={"Buy Course"} />
				}
						</ButtonView>) : "Added to Cart"}

			</React.Fragment>
		)
	}
};


// checkout

// add to calendar
export const addToCalendar: IDataFragment<ICourseFragmentProps> = {
	name: "Add to Calendar",
	label: {k: "course.addToCalendar", v: "Add to Calendar"},
	component: ({course, actions, label}) => (
		<PageLinkView
			viewKey="catalog.detailsLink.link"
			render={({path}) => {

				return (
					<ButtonView variant={ButtonVariant.PRIMARY} viewKey="catalog.addToCalendar" onClick={() => actions.addToCalendar(course, path)}>
						<CatalogLabels.Labels.AddToCalendar />
					</ButtonView>
				);
			}}
		/>
	)
};

// more info
export const moreInfo: IDataFragment<ICourseFragmentProps> = {
	name: "More Info",
	label: {k: "course.moreInfo", v: "More Info"},
	component: ({course}) => {
		return (
			course && course.infoUrl && !course.canLaunch &&
				<a href={course.infoUrl} target="_blank" rel="noopener noreferrer">
					<ButtonView variant={ButtonVariant.PRIMARY} viewKey="catalog.moreInfoLink">
						<CatalogLabels.Labels.MoreInfo />
					</ButtonView>
			  </a>
		);
		}
};

// intel add to calendar
export const intelAddToCalendar: IDataFragment<ICourseFragmentProps> = {
	name: "Intel Add to Calendar",
	label: {k: "course.intel.addToCalendar", v: "Add to Calendar"},
	component: ({course, actions, label}) => (
		<PageLinkView
			viewKey="catalog.detailsLink.link"
			render={({path}) => {

				return (
					<ButtonView variant={ButtonVariant.PRIMARY}
						viewKey="catalog.addToCalendar"
						onClick={() => actions.addToCalendar(course, path)}
						attrs={course && {
							'data-wap_type': 'pt-add-to-calender',
							'data-wap_ref': `${course.itemExternalId}:${course.title}`,
							'data-wap': JSON.stringify({ 'user_id': TokenSession.getUserId(),
													'email_id': TokenSession.getUser().email,
													'course_id':course.itemExternalId,
													'course_name':course.title,
													'start_date':course.startDate})
						}}>
						<CatalogLabels.Labels.AddToCalendar />
					</ButtonView>
				);
			}}
		/>
	)
};

export const extendSubscription: IDataFragment<ICourseFragmentProps> = {
	name: "Extend Subscription",
	label: {k: "subscription.extend", v: "Extend Subscription"},
	enabled: ({course}) =>  course && course.canExtend && course.canExtend!= undefined ? course.canExtend: false,
	component: ({course, actions, disableDirectBuy}) => (<ExtendSubscription course={course} actions={actions} disableDirectBuy={disableDirectBuy} />)

};

// add to bookmark
export const addToBookmark: IDataFragment<ICourseFragmentProps> = {
	name: "Add to Bookmark",
	label: {k: "course.addToBookmark", v: "Add to Bookmark List"},
	component: ({course}) => {
				var msg = "+Add To List";
				if(course && course.bookmarked){
					msg = "-Remove from List";
				}
				if (!course) {
					return <ButtonView variant={ButtonVariant.PRIMARY} viewKey="course.addToBookmark">{'...'}</ButtonView>;
				}
				return (
					<MutationButton
						variant={ButtonVariant.MINIMAL}
						viewKey="course.addToBookmark"
						clazz={CourseBookmark}
						params={{id: Number(course.metaId)}}
						clearCacheOnSuccess={true}>
						<CatalogLabels.Labels.CourseBookmark args={{title: msg}} />
					</MutationButton>
				);
	}

};


export const userRating: IDataFragment<ICourseFragmentProps> = {
	name: "User Rating",
	label: {k: "course.user.rating", v: "User Rating"},

	component: ({course}) => {
		const [userItemRating, setUserItemRating]= useState(course.userItemRating)
		const handleClick = (value) =>{
			setUserItemRating(value);
		}

		return (
			<React.Fragment>
				<BlockView variant={BlockVariant.PAGING} viewKey="userrating">
				<Label k="course.userRating" v="User rating: "/>
				<MutationButton
					viewKey="course.userrating.button"
					clazz={RateItem}
					params={{itemId: course.itemId, rating: userItemRating}}
					method={MutationType.UPDATE}
					clearCacheOnSuccess={true}
						onFailure={(error) => {
							console.log("Rating not saved:", error);
						}}
				>
					<Rating
						emptySymbol="far fa-star fa-1x medium"
						fullSymbol="fas fa-star fa-1x medium"
						fractions={2}
						initialRating={userItemRating}
						direction='ltr'
						onClick={(value) => {handleClick(value)}}
					/>
				</MutationButton>
				</BlockView>
			</React.Fragment>
		)
	}
};

export const contributorLink: IDataFragment<ICourseFragmentProps> = {
	name: "Contributor Catalog Link",
	label: { k: "Contributor.catalogs.link", v: "Contributor Catalog Link" },
	enabled: ({ course, productContributor }) => (productContributor && productContributor.length),
	component: ({ course, productContributor }) => {
		return (
			productContributor.map((contri) => (
				<PageLinkView
					attrs={contri && {
						'data-wap_ref': `${contri.contributor.id}:${contri.contributor.name}`
					}}
					params={{ contributorId: contri && contri.contributor && contri.contributor.id }}
					viewKey="contributor.catalogs.link"
					breadcrumbs={(target, breadcrumbs) => {
						let nextCrumbs: IBreadcrumb[] = [];
						// push current page's crumb
						const lastCrumb = lastItem(breadcrumbs);

						const currentPath = UrlUtil.getCurrentPath();
						// if last crumb was for the current page and has a target, use that one
						if (
							lastCrumb &&
							lastCrumb.target.pathname === currentPath
						) {
							nextCrumbs = [
								...breadcrumbs.slice(0, breadcrumbs.length - 1),
								{
									...lastCrumb,
									target: {
										pathname: currentPath,
										search: UrlUtil.getSearch()
									}
								}
							];
						} else if ( // if last crumb was for the current page and has no target path associated
							lastCrumb &&
							lastCrumb.target.pathname === ''
						) {
							nextCrumbs = [
								...breadcrumbs.slice(0, breadcrumbs.length - 1),
								{
									...lastCrumb,
									target: {
										pathname: currentPath,
										search: UrlUtil.getSearch()
									}
								}
							];
						} else {
							// else create a new one for the current page
							nextCrumbs = [
								...breadcrumbs,
								{
									key: "contributor.catalogs.link",
									label: {
										k: contri && contri.contributor && contri.contributor.id,
										v: window.parent.location.pathname.slice(1) || "Home"
									},
									target: {
										pathname: currentPath,
										search: UrlUtil.getSearch()
									}
								},
							];
						}

						// push article crumb
						nextCrumbs.push({
							key: contri && contri.contributor && contri.contributor.id,
							label: {
								k: contri && contri.contributor && contri.contributor.id,
								v: course.title,
							},
							//target
							target: {
								pathname: '',
								search: ''
							}
						});

						return nextCrumbs;
					}}
				>
					<div>
						<ButtonView variant={ButtonVariant.SECONDARY} viewKey="contributor.catalogLink.button">
							<TypographyView viewKey="contributor.name" html={contri && contri.contributor && contri.contributor.name} />
						</ButtonView>
					</div>
				</PageLinkView>
			))
		);
	}
};




// WEBPACK FOOTER //
// ./src/panels/catalog/fragments/course/actionFragments.tsx